/**
 * Блоки «Обзора района» в разметке K2 — общие для фикстурной ветки
 * (`OverviewFixtures`) и канона (`OverviewApi`).
 *
 * Решение 10 плана `astra-on-canon`: переключение экрана на канон — замена
 * реализации, а не переписывание экрана. Поэтому разметка (блоки, порядок,
 * классы, подписи) живёт здесь один раз, а обе ветки только собирают для неё
 * данные. Чего у источника нет, блок получает как `null` и говорит «нет
 * данных» с причиной словами — ни нулём, ни догадкой (отсутствие ≠ ноль).
 */

import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  Database,
  ListChecks,
  MessageSquareOff,
  ScanSearch,
  Sprout,
  Timer,
  type LucideIcon,
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { DoneSplit } from '../ui/DoneSplit';
import { DataTable } from '../ui/DataTable';
import { EmptyState } from '../ui/EmptyState';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Icon } from '../ui/Icon';
import { MiniBar } from '../ui/Spark';
import { MapWidget } from '../map/MapWidget';
import type { TrackData } from '../data/useTrackData';
import { tokenColor } from '../styles/palette';
import {
  countOf,
  ha,
  haUnit,
  num,
  windowLabel,
  WORDS,
} from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { COMMON } from '../../i18n/common';
import { tr } from '../../i18n/runtime';
import { DENSITY } from '../lib/levels';
import type { AttentionKind, ContourMeta, Crop, Cycle, LevelName, Massif, Work } from '../data/types';

/* ------------------------------------------------------------- тексты */

const m = defineMessages({
  levelDistrict: { id: 'district.parts.level.district', defaultMessage: 'Район', description: 'Подпись уровня «район» у позиции очереди внимания; коротко' },
  levelMassif: { id: 'district.parts.level.massif', defaultMessage: 'Массив', description: 'Подпись уровня «массив» у позиции очереди внимания; коротко' },
  levelFarm: { id: 'district.parts.level.farm', defaultMessage: 'Хозяйство', description: 'Подпись уровня «хозяйство» у позиции очереди внимания; коротко' },
  levelContour: { id: 'district.parts.level.contour', defaultMessage: 'Контур', description: 'Подпись уровня «контур» у позиции очереди внимания; коротко' },

  daysWord: {
    id: 'district.parts.daysWord',
    defaultMessage: '{count, plural, one {день} few {дня} many {дней} other {дня}}',
    description: 'Слово «день» под числом в строке ближайшего окна: «3 дня»; без числа, склоняется по {count}',
  },
  daysAgo: { id: 'district.parts.daysAgo', defaultMessage: 'дн. назад', description: 'Подпись под числом в строке окна, когда срок окна уже прошёл: «2 дн. назад»; узкая колонка' },
  deadlineUnknown: { id: 'district.parts.deadlineUnknown', defaultMessage: 'срок неизвестен', description: 'Подпись вместо числа дней в строке окна, когда дата конца окна неизвестна; узкая колонка' },

  active: { id: 'district.parts.active.badge', defaultMessage: 'идёт', description: 'Бейдж у названия активного агроцикла: цикл идёт; строчными, коротко' },
  stageNow: { id: 'district.parts.active.stageNow', defaultMessage: 'Этап сейчас', description: 'Подпись над названием текущего этапа агроцикла в карточке активного агроцикла' },
  stageUnnamed: { id: 'district.parts.active.stageUnnamed', defaultMessage: 'этап не назван', description: 'Вместо названия текущего этапа, когда источник его не называет; строчными' },
  stageNoWindow: { id: 'district.parts.active.stageNoWindow', defaultMessage: 'окно этапа не выведено', description: 'Вместо окна текущего этапа, когда его не из чего вывести; строчными' },
  spanAria: {
    id: 'district.parts.active.spanAria',
    defaultMessage: 'Окно агроцикла: прошло {gone} из {span, plural, one {# дня} few {# дней} many {# дней} other {# дня}}',
    description: 'aria-label полоски хода календарного окна агроцикла для чтеца с экрана; {gone} — число прошедших дней, {span} — длина окна в днях',
  },
  cycleFoot: {
    id: 'district.parts.active.foot',
    defaultMessage:
      'Окно агроцикла {window}{hasGone, select, yes {: прошло {gone} из {span, plural, one {# дня} few {# дней} many {# дней} other {# дня}}} other {}}{hasLeft, select, yes {, остаётся {left, plural, one {# день} few {# дня} many {# дней} other {# дня}}.} other {.}} Линия — про календарь, а не про выполнение. Окно этапа выведено из работ этого этапа, а не из шаблона.',
    description: 'Пояснение под полоской календарного окна агроцикла; {window} — «1 апр — 15 окт 2026», {hasGone}/{hasLeft} — yes/no, {gone} — прошло дней (строка), {span} — длина окна в днях, {left} — осталось дней',
  },
  doneDenomCycle: {
    id: 'district.parts.active.denomCycle',
    defaultMessage: '{count, plural, one {работа на поле агроцикла} few {работы на полях агроцикла} many {работ на полях агроцикла} other {работы на полях агроцикла}}',
    description: 'Слово-знаменатель после числа под полосой «Выполнено» в карточке активного агроцикла: «из 1 145 работ на полях агроцикла»; без числа',
  },
  doneDenomOpened: {
    id: 'district.parts.active.denomOpened',
    defaultMessage: '{count, plural, one {работа на поле в открытых окнах} few {работы на полях в открытых окнах} many {работ на полях в открытых окнах} other {работы на полях в открытых окнах}}',
    description: 'Слово-знаменатель под полосой в карточке активного агроцикла: работы только тех окон, что уже открывались; без числа',
  },
  doneDenomPlain: {
    id: 'district.parts.active.denomPlain',
    defaultMessage: '{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}',
    description: 'Слово-знаменатель под пустой полосой в карточке активного агроцикла: «работ на полях»; без числа',
  },
  worksFileUnread: { id: 'district.parts.active.worksUnread', defaultMessage: 'данные работ агроцикла не прочитаны: {reason}', description: 'Причина пустой полосы в карточке активного агроцикла; {reason} — почему не прочитано' },
  noWindowsOpened: { id: 'district.parts.active.noWindowsOpened', defaultMessage: 'ни одно окно работ этого агроцикла ещё не открывалось — считать нечего', description: 'Причина пустой полосы в карточке активного агроцикла: окна ещё впереди' },
  windowsRunning: {
    id: 'district.parts.active.windowsRunning',
    defaultMessage: '{count, plural, one {# окно идёт} few {# окна идут} many {# окон идут} other {# окна идут}} прямо сейчас — по ним отчёт ещё в срок.',
    description: 'Пояснение под полосой «Выполнено»: сколько окон работ идёт сейчас; {count} — число окон',
  },
  aheadWorks: { id: 'district.parts.active.aheadWorks', defaultMessage: 'Ещё {works} впереди: их окна не открывались, в знаменатель они не входят.', description: 'Пояснение под полосой «Выполнено»; {works} — число работ на полях с единицей («5 работ на полях»)' },
  worksLoading: { id: 'district.parts.active.worksLoading', defaultMessage: 'Работы агроцикла загружаются…', description: 'Заглушка в карточке активного агроцикла, пока грузятся работы' },
  closingTitle: { id: 'district.parts.active.closingTitle', defaultMessage: 'Ближайшие закрывающиеся окна', description: 'Подзаголовок списка окон работ, которые закроются раньше других (в карточке активного агроцикла)' },
  windowsLoading: { id: 'district.parts.active.windowsLoading', defaultMessage: 'Окна работ загружаются…', description: 'Заглушка в списке ближайших окон, пока грузятся данные' },
  noRunningTitle: { id: 'district.parts.active.noRunning.title', defaultMessage: 'Идущих окон нет', description: 'Заголовок пустого состояния списка ближайших окон работ' },
  noRunningReason: { id: 'district.parts.active.noRunning.reason', defaultMessage: 'на дату среза ни одно окно работ не открыто: ближайшие ещё впереди', description: 'Пояснение в пустом состоянии «Идущих окон нет»; строчными' },
  worksListUnread: { id: 'district.parts.active.worksListUnread', defaultMessage: 'список работ агроцикла не прочитан: {reason}', description: 'Пояснение в пустом состоянии списка окон, когда список работ не получен; {reason} — причина' },
  windowSub: { id: 'district.parts.active.windowSub', defaultMessage: '{stage} · окно {window}', description: 'Мелкая строка под названием работы в списке окон; {stage} — этап агроцикла, {window} — окно дат' },

  closedBadge: { id: 'district.parts.closed.badge', defaultMessage: 'закрыт', description: 'Бейдж у названия закрытого агроцикла; строчными, коротко' },
  closedGo: { id: 'district.parts.closed.go', defaultMessage: 'Итоги агроцикла', description: 'Ссылка в строке закрытого агроцикла на страницу итогов; коротко' },
  closedProgress: {
    id: 'district.parts.closed.progress',
    defaultMessage: '{done} из {total, plural, one {# окна} few {# окон} many {# окон} other {# окна}} закрыто',
    description: 'Ход закрытия окон работ в закрытом агроцикле; {done} — число закрытых окон (строка), {total} — всего окон',
  },
  closedProgressUnknown: { id: 'district.parts.closed.progressUnknown', defaultMessage: 'закрытых окон: нет данных — источник хода по окнам не называет', description: 'Вместо хода закрытия окон, когда источник его не называет' },
  closedFacts: {
    id: 'district.parts.closed.facts',
    defaultMessage: '{window} · {area} · {farms} · {contours} · {progress}',
    description: 'Строка фактов закрытого агроцикла; {window} — окно дат, {area} — «1 200 га», {farms} — «24 хозяйства», {contours} — «300 контуров», {progress} — ход закрытия окон',
  },

  frameTitle: { id: 'district.parts.map.title', defaultMessage: 'Кадр карты: размещение 2026', description: 'Заголовок карточки с картой на «Обзоре района»: контуры, окрашенные по культуре размещения 2026' },
  frameDerived: { id: 'district.parts.map.derived', defaultMessage: 'Геометрия производная', description: 'Метка доверия в заголовке карточки с картой: геометрия контуров получена из данных, а не измерена' },
  frameCaption: { id: 'district.parts.map.caption', defaultMessage: 'Цвет полигона — культура размещения 2026', description: 'Подпись под картой: что означает цвет контура' },
  frameOrigin: { id: 'district.parts.map.origin', defaultMessage: 'Происхождение геометрии', description: 'Подзаголовок блока справа от карты: откуда взята геометрия контуров' },
  frameSimplified: { id: 'district.parts.map.simplified', defaultMessage: 'Полигоны упрощены для веба — квантование координат и прореживание вершин, поэтому площади берутся из реестра, а не измеряются по полигону.', description: 'Пояснение справа от карты: почему площади берутся из реестра' },
  frameClick: { id: 'district.parts.map.click', defaultMessage: 'Нажатие на контур открывает его карточку, не уводя со страницы: хозяйство по имени и переход в полную карточку контура.', description: 'Пояснение справа от карты: что происходит при нажатии на контур' },
  frameTop: { id: 'district.parts.map.top', defaultMessage: 'Больше всего земли', description: 'Подзаголовок блока справа от карты: культуры с самой большой площадью' },
  frameTopOf: {
    id: 'district.parts.map.topOf',
    defaultMessage: 'Верхние {top} из {crops}: полная разбивка — раскрытием ниже.',
    description: 'Пояснение под списком культур справа от карты; {top} — сколько показано, {crops} — «12 культур» (всего)',
  },
  frameAll: { id: 'district.parts.map.all', defaultMessage: 'Все {crops} среза. Цвет точки совпадает с легендой карты.', description: 'Пояснение под списком культур справа от карты, когда показаны все; {crops} — «5 культур»' },

  cropsNoneTitle: { id: 'district.parts.crops.noneTitle', defaultMessage: 'Размещения нет', description: 'Заголовок пустого состояния разбивки по культурам' },
  cropsChartLabel: { id: 'district.parts.crops.chartLabel', defaultMessage: 'Площадь по культурам: {list}', description: 'aria-label столбчатой диаграммы площади по культурам; {list} — перечисление «Хлопок 120 га, Пшеница 80 га»' },
  cropsChartItem: { id: 'district.parts.crops.chartItem', defaultMessage: '{name} {ha} га', description: 'Элемент перечисления в aria-label диаграммы; {name} — культура, {ha} — гектары земли' },
  cropsChartNote: { id: 'district.parts.crops.chartNote', defaultMessage: 'Высота столбика — площадь под культурой, порядок тот же, что в строках. Цвет столбика — шаг зелёной рампы по величине, своего смысла у него нет. Смысл несёт точка в строке: её цвет совпадает с легендой карты.', description: 'Пояснение под диаграммой площади по культурам: как читать цвет и высоту' },
  noData: { id: 'district.parts.noData', defaultMessage: 'нет данных', description: 'Вместо числа в ячейке таблицы или строке, когда источник значения не назвал; честное «нет данных», а не 0' },

  massifColName: { id: 'district.parts.massifs.col.name', defaultMessage: 'Массив', description: 'Заголовок колонки таблицы массивов: название массива' },
  massifColContours: { id: 'district.parts.massifs.col.contours', defaultMessage: 'Контуры', description: 'Заголовок колонки таблицы массивов: число контуров' },
  massifColFarms: { id: 'district.parts.massifs.col.farms', defaultMessage: 'Хозяйства', description: 'Заголовок колонки таблицы массивов: число хозяйств' },
  massifColArea: { id: 'district.parts.massifs.col.area', defaultMessage: 'Площадь', description: 'Заголовок колонки таблицы массивов: площадь; над единицей' },
  massifUnitHa: { id: 'district.parts.massifs.unitHa', defaultMessage: 'га', description: 'Единица под заголовком колонки «Площадь»: гектары земли; узкая колонка' },
  massifDenom: {
    id: 'district.parts.massifs.denominator',
    defaultMessage: '{count, plural, one {массив} few {массива} many {массивов} other {массива}}',
    description: 'Слово-знаменатель под таблицей массивов: «5 массивов»; без числа, склоняется по {count}',
  },
  massifEmptyTitle: { id: 'district.parts.massifs.emptyTitle', defaultMessage: 'Массивов нет', description: 'Заголовок пустого состояния таблицы массивов' },
});

/** Подпись уровня позиции очереди внимания — на языке интерфейса (вызывать при рендере). */
export function levelCaption(level: LevelName): string {
  const byLevel = {
    district: m.levelDistrict,
    massif: m.levelMassif,
    farm: m.levelFarm,
    contour: m.levelContour,
  } as const;
  return tr(byLevel[level]);
}

/** Значок вида пункта внимания; у пункта без вида — нейтральный значок. */
export const KIND_ICON: Record<AttentionKind, LucideIcon> = {
  late_work: CalendarClock,
  silent_farm: MessageSquareOff,
  window_closing: Timer,
  data_gap: Database,
  quarantine_watch: ScanSearch,
};

/* ------------------------------------------------------------ календарь */

function utcOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const parts = iso.slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  return Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!);
}

/** Дней от `from` до `to`; null — если хотя бы одна дата не разобралась. */
export function daysBetween(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  const a = utcOf(from);
  const b = utcOf(to);
  if (a === null || b === null) return null;
  return Math.round((b - a) / 86_400_000);
}

/* ---------------------------------------------------------- агрегаты */

export interface CycleRollup {
  /** Знаменатель двух осей: обязательства окон, которые уже открывались. */
  openedTotal: number;
  openedReported: number;
  openedVerified: number;
  /** Обязательства неоткрытых окон — в знаменатель не входят и называются числом. */
  aheadObligations: number;
  doneWorks: number;
  activeWorks: number;
  upcomingWorks: number;
  /** Идущие окна по возрастанию даты конца. */
  closing: Work[];
  /** Окно стадии, выведенное из работ этой стадии. */
  stageFrom: string | null;
  stageTo: string | null;
}

export function rollupWorks(works: Work[], stageNow: string | null): CycleRollup {
  const roll: CycleRollup = {
    openedTotal: 0,
    openedReported: 0,
    openedVerified: 0,
    aheadObligations: 0,
    doneWorks: 0,
    activeWorks: 0,
    upcomingWorks: 0,
    closing: [],
    stageFrom: null,
    stageTo: null,
  };

  for (const work of works) {
    if (work.status === 'upcoming') {
      roll.upcomingWorks += 1;
      roll.aheadObligations += work.totals.obligations;
    } else {
      roll.openedTotal += work.totals.obligations;
      roll.openedReported += work.totals.reported;
      roll.openedVerified += work.totals.verified;
      if (work.status === 'done') roll.doneWorks += 1;
      else roll.activeWorks += 1;
    }

    // Работа без окна (канон: срок не назначен) окна стадии не двигает.
    if (stageNow && work.stage === stageNow && work.window.from && work.window.to) {
      if (!roll.stageFrom || work.window.from < roll.stageFrom) roll.stageFrom = work.window.from;
      if (!roll.stageTo || work.window.to > roll.stageTo) roll.stageTo = work.window.to;
    }
  }

  roll.closing = works
    .filter((w) => w.status === 'active')
    .sort((a, b) => (a.window.to < b.window.to ? -1 : a.window.to > b.window.to ? 1 : 0))
    .slice(0, 3);

  return roll;
}

/* ------------------------------------------------------ карточка цикла */

export function ActiveCycle({
  cycle,
  title,
  asOf,
  worksRes,
  obligations,
}: {
  cycle: Cycle;
  title: string;
  asOf: string | null;
  worksRes: TrackData<Work[]>;
  /**
   * Обязательства цикла из картины сезона (канон). Когда переданы, полоса
   * считается по ним — тем же определением «доли отчётов», что на главной и
   * в сводке к совещанию (`lib/reportShare.ts`), а не по открытым окнам файла
   * работ: два знаменателя одной доли читались как два разных района.
   */
  obligations?: { total: number; reported: number; verified: number } | null;
}) {
  const t = useT();
  const works = useMemo(() => worksRes.data ?? [], [worksRes.data]);
  const roll = useMemo(() => rollupWorks(works, cycle.stage_now), [works, cycle.stage_now]);

  const spanDays = daysBetween(cycle.window.from, cycle.window.to);
  const goneDays = daysBetween(cycle.window.from, asOf);
  const leftDays = daysBetween(asOf, cycle.window.to);
  const ratio =
    spanDays !== null && goneDays !== null && spanDays > 0
      ? Math.min(100, Math.max(0, (goneDays / spanDays) * 100))
      : null;

  return (
    <div className="ov-cycle">
      <div className="ov-cycle__top">
        <span className="ov-cycle__name">
          <Link to={`/cycles/${cycle.id}`}>{title}</Link>
        </span>
        <Badge tone="sage" icon={Sprout}>
          {t(m.active)}
        </Badge>
        <span className="ov-cycle__meta">
          {haUnit(cycle.area_ha)} · {countOf(cycle.farms, WORDS.farm)} ·{' '}
          {countOf(cycle.contours, WORDS.contour)}
        </span>
      </div>

      <div className="ov-stage">
        <div className="ov-stage__head">
          <span className="ov-stage__label">{t(m.stageNow)}</span>
          <span className="ov-stage__name">{cycle.stage_now ?? t(m.stageUnnamed)}</span>
          <span className="ov-stage__win">
            {roll.stageFrom && roll.stageTo
              ? windowLabel(roll.stageFrom, roll.stageTo)
              : t(m.stageNoWindow)}
          </span>
        </div>

        {ratio === null || spanDays === null || goneDays === null ? null : (
          <div
            className="ov-span"
            role="img"
            aria-label={t(m.spanAria, { gone: num(goneDays), span: spanDays })}
          >
            <div className="ov-span__past" style={{ width: `${ratio}%` }} />
          </div>
        )}

        <div className="ov-stage__foot">
          {t(m.cycleFoot, {
            window: windowLabel(cycle.window.from || null, cycle.window.to || null),
            hasGone: goneDays !== null && spanDays !== null ? 'yes' : 'no',
            gone: goneDays !== null ? num(goneDays) : '',
            span: spanDays ?? 0,
            hasLeft: leftDays !== null ? 'yes' : 'no',
            left: leftDays ?? 0,
          })}
        </div>
      </div>

      {obligations ? (
        <DoneSplit
          counts={obligations}
          denominator={m.doneDenomCycle}
        />
      ) : worksRes.state === 'loading' ? (
        <div className="ov-note">{t(m.worksLoading)}</div>
      ) : roll.openedTotal === 0 ? (
        <DoneSplit
          counts={null}
          denominator={m.doneDenomPlain}
          reason={
            worksRes.state === 'missing'
              ? t(m.worksFileUnread, { reason: worksRes.reason ?? t(COMMON.noReason) })
              : t(m.noWindowsOpened)
          }
        />
      ) : (
        <DoneSplit
          counts={{
            total: roll.openedTotal,
            reported: roll.openedReported,
            verified: roll.openedVerified,
          }}
          denominator={m.doneDenomOpened}
          windowNote={[
            roll.activeWorks > 0 ? t(m.windowsRunning, { count: roll.activeWorks }) : null,
            roll.aheadObligations > 0
              ? t(m.aheadWorks, { works: countOf(roll.aheadObligations, WORDS.obligation) })
              : null,
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )}

      <div>
        <div className="ov-sub">{t(m.closingTitle)}</div>
        {worksRes.state === 'loading' ? (
          <div className="ov-note">{t(m.windowsLoading)}</div>
        ) : roll.closing.length === 0 ? (
          <EmptyState
            compact
            icon={Timer}
            title={t(m.noRunningTitle)}
            reason={
              worksRes.state === 'ready'
                ? t(m.noRunningReason)
                : t(m.worksListUnread, { reason: worksRes.reason ?? t(COMMON.noReason) })
            }
          />
        ) : (
          <div className="ov-wins">
            {roll.closing.map((work) => {
              const left = daysBetween(asOf, work.window.to);
              return (
                <div className="ov-wins__row" key={work.id}>
                  <span className="ov-wins__days">
                    <span className="ov-wins__num">
                      {left === null ? '—' : num(Math.abs(left))}
                    </span>
                    <span className="ov-wins__word">
                      {left === null
                        ? t(m.deadlineUnknown)
                        : left < 0
                          ? t(m.daysAgo)
                          : t(m.daysWord, { count: left })}
                    </span>
                  </span>
                  <span className="ov-wins__main">
                    <span className="ov-wins__name">
                      <Link to={`/cycles/${cycle.id}/works/${work.id}`}>{work.name}</Link>
                    </span>
                    <span className="ov-wins__sub">
                      {t(m.windowSub, { stage: work.stage, window: windowLabel(work.window.from, work.window.to) })}
                    </span>
                  </span>
                  <span className="ov-wins__vol">
                    <span>{haUnit(work.totals.area_ha)}</span>
                    <span>{countOf(work.totals.obligations, WORDS.obligation)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------- закрытый цикл */

export function ClosedCycleRow({
  cycleId,
  title,
  facts,
}: {
  cycleId: string;
  title: string;
  facts: string;
}) {
  const t = useT();
  return (
    <div className="ov-closed">
      <span className="ov-closed__name">
        <Link to={`/cycles/${cycleId}`}>{title}</Link>
      </span>
      <Badge tone="outline">{t(m.closedBadge)}</Badge>
      <span className="ov-closed__facts">{facts}</span>
      <Link className="linkish ov-closed__go" to={`/cycles/${cycleId}`}>
        {t(m.closedGo)}
      </Link>
    </div>
  );
}

/** Факты закрытого цикла строкой; ход окон, которого источник не назвал, — словами. */
export function closedCycleFacts(cycle: Cycle): string {
  const done = cycle.progress.works_done;
  const total = cycle.progress.works_total;
  const progress =
    Number.isFinite(done) && Number.isFinite(total)
      ? tr(m.closedProgress, { done: num(done), total })
      : tr(m.closedProgressUnknown);
  return tr(m.closedFacts, {
    window: windowLabel(cycle.window.from || null, cycle.window.to || null),
    area: haUnit(cycle.area_ha),
    farms: countOf(cycle.farms, WORDS.farm),
    contours: countOf(cycle.contours, WORDS.contour),
    progress,
  });
}

/* -------------------------------------------------- очередь внимания */

export interface QueueItemView {
  key: string;
  /** Вид пробела; null — источник вида не называет, значок нейтральный. */
  kind: AttentionKind | null;
  /** Подпись уровня; null — уровня у пункта нет. */
  level: string | null;
  title: string;
  href: string | null;
  body: ReactNode;
  foot: ReactNode;
}

export function QueueItem({ item }: { item: QueueItemView }) {
  return (
    <li className="ov-queue__item">
      {/* Тон квадрата называет ВИД пробела (см. `.attn--*` в pages.css);
          фон карточки остаётся нейтральным — оценки хозяйства тут нет. */}
      <span
        className={item.kind ? `ov-queue__icon attn attn--${item.kind}` : 'ov-queue__icon'}
        aria-hidden="true"
      >
        <Icon icon={item.kind ? KIND_ICON[item.kind] : ListChecks} size={16} />
      </span>
      <span className="ov-queue__body">
        <span className="ov-queue__top">
          {item.level ? <span className="ov-queue__level">{item.level}</span> : null}
          <span className="ov-queue__title">
            {item.href ? <Link to={item.href}>{item.title}</Link> : item.title}
          </span>
        </span>
        <span className="ov-queue__text">{item.body}</span>
        <span className="ov-queue__foot">{item.foot}</span>
      </span>
    </li>
  );
}

/* ------------------------------------------------------ кадр карты */

export interface CropSlice {
  id: string;
  name: string;
  color: string;
  /** Контуров под культурой; null — источник счёт контуров не называет. */
  contours: number | null;
  area: number;
}

export function MapFrameCard({
  crops,
  massifs,
  onSelect,
  geometryNote,
  cropSlices,
  cropsEmptyNote,
  aside,
}: {
  crops: Crop[] | undefined;
  massifs: Massif[] | undefined;
  /** Уведомление о выборе; карточку контура открывает сама карта. */
  onSelect?: (contour: ContourMeta) => void;
  geometryNote: ReactNode;
  cropSlices: CropSlice[];
  cropsEmptyNote: string;
  aside?: ReactNode;
}) {
  const t = useT();
  return (
    <Card
      size="lg"
      title={t(m.frameTitle)}
      aside={aside ?? <EvidenceMark kind="derived" label={t(m.frameDerived)} />}
    >
      <div className="mapsplit">
        <div className="mapsplit__frame">
          <MapWidget
            colorBy="crop"
            crops={crops}
            massifLabels={massifs}
            mode="frame"
            height="100%"
            caption={t(m.frameCaption)}
            onSelect={(contour) => {
              if (contour) onSelect?.(contour);
            }}
          />
        </div>

        <div className="mapsplit__aside">
          <div className="mapaside__block">
            <div className="mapaside__title">{t(m.frameOrigin)}</div>
            <div className="mapaside__note">{geometryNote}</div>
            <div className="mapaside__note">{t(m.frameSimplified)}</div>
            <div className="mapaside__note">{t(m.frameClick)}</div>
          </div>

          <div className="mapaside__block">
            <div className="mapaside__title">{t(m.frameTop)}</div>
            {cropSlices.length === 0 ? (
              <div className="mapaside__note">{cropsEmptyNote}</div>
            ) : (
              <>
                <div className="toplist">
                  {cropSlices.slice(0, DENSITY.listItems).map((slice) => (
                    <div className="toplist__row" key={slice.id}>
                      <span
                        className="toplist__dot"
                        style={{ background: tokenColor(slice.color) }}
                        aria-hidden="true"
                      />
                      <span className="toplist__name">{slice.name}</span>
                      <span className="toplist__val">{haUnit(slice.area)}</span>
                    </div>
                  ))}
                </div>
                <div className="mapaside__note">
                  {cropSlices.length > DENSITY.listItems
                    ? t(m.frameTopOf, { top: num(DENSITY.listItems), crops: countOf(cropSlices.length, WORDS.crop) })
                    : t(m.frameAll, { crops: countOf(cropSlices.length, WORDS.crop) })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------ разбивка по культурам */

export function CropsBreakdown({
  placed,
  emptyReason,
  unplacedNote,
  contoursNote,
}: {
  placed: CropSlice[];
  emptyReason: string;
  /** Строка о контурах без культуры; null — сказать нечего. */
  unplacedNote: string | null;
  /** Почему в колонке контуров «нет данных»; null — счёт есть. */
  contoursNote?: string | null;
}) {
  const t = useT();
  if (placed.length === 0) {
    return <EmptyState compact title={t(m.cropsNoneTitle)} reason={emptyReason} />;
  }
  return (
    <div className="ov-crops">
      <div className="ov-crops__chart">
        <MiniBar
          values={placed.map((s) => s.area)}
          names={placed.map((s) => s.name)}
          width={280}
          height={56}
          label={t(m.cropsChartLabel, {
            list: placed.map((s) => t(m.cropsChartItem, { name: s.name, ha: ha(s.area) })).join(', '),
          })}
        />
        <span className="ov-note">{t(m.cropsChartNote)}</span>
      </div>

      <div className="ov-rows">
        {placed.map((slice) => (
          <div className="ov-crop" key={slice.id}>
            <span
              className="ov-crop__dot"
              style={{ background: tokenColor(slice.color) }}
              aria-hidden="true"
            />
            <span>{slice.name}</span>
            <span className="ov-crop__ha">{haUnit(slice.area)}</span>
            <span className="ov-crop__n">
              {slice.contours === null ? t(m.noData) : countOf(slice.contours, WORDS.contour)}
            </span>
          </div>
        ))}
      </div>

      {contoursNote ? <div className="ov-note">{contoursNote}</div> : null}
      {unplacedNote ? <div className="ov-note">{unplacedNote}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------- массивы */

export interface MassifRow {
  id: string;
  name: string;
  contours: number;
  /** Хозяйств в массиве; null — источник не назвал. */
  farms: number | null;
  /** Площадь, га; null — источник не назвал. */
  area: number | null;
}

export function MassifTable({ rows, emptyReason }: { rows: MassifRow[]; emptyReason: string }) {
  const t = useT();
  return (
    <DataTable
      columns={[
        {
          key: 'name',
          header: t(m.massifColName),
          render: (row: MassifRow) => <Link to={`/massifs/${row.id}`}>{row.name}</Link>,
        },
        {
          key: 'contours',
          header: t(m.massifColContours),
          numeric: true,
          render: (row: MassifRow) => num(row.contours),
        },
        {
          key: 'farms',
          header: t(m.massifColFarms),
          numeric: true,
          render: (row: MassifRow) => (row.farms === null ? t(m.noData) : num(row.farms)),
        },
        {
          key: 'area',
          header: t(m.massifColArea),
          unit: t(m.massifUnitHa),
          numeric: true,
          render: (row: MassifRow) => (row.area === null ? t(m.noData) : ha(row.area)),
        },
      ]}
      rows={rows}
      getKey={(row) => row.id}
      denominator={m.massifDenom}
      empty={{ title: t(m.massifEmptyTitle), reason: emptyReason }}
    />
  );
}

/* ------------------------------------------------ готовность данных */

export function ReadyRow({
  name,
  fact,
  mark,
}: {
  name: string;
  fact: ReactNode;
  mark?: ReactNode;
}) {
  return (
    <div className="ov-ready__row">
      <span className="ov-ready__main">
        <span className="ov-ready__name">{name}</span>
        <span className="ov-ready__fact">{fact}</span>
      </span>
      {mark ?? null}
    </div>
  );
}
